import { z } from 'zod';

import { Currency, PriceInterval } from '../billing';
import { ProductKind } from '../me';
import { ApiOk, I18nText, Page, PageQuery, Ulid } from '../primitives';
import { Timestamps } from './common';

export const CmsPriceDTO = z
  .object({
    id: Ulid,
    productId: z.string(),
    interval: PriceInterval,
    amountMinor: z.number().int().nonnegative(),
    currency: Currency,
    stripePriceId: z.string().nullable(),
    active: z.boolean(),
  })
  .merge(Timestamps);
export type CmsPriceDTO = z.infer<typeof CmsPriceDTO>;

/** `products` row with its prices (docs/02 §1). `id` is a slug (`plan_standard`). */
export const CmsProductDTO = z
  .object({
    id: z.string(),
    kind: ProductKind,
    name: I18nText,
    description: I18nText,
    features: z.array(z.string()),
    requiresPlan: z.boolean(),
    active: z.boolean(),
    sort: z.number().int(),
    stripeProductId: z.string().nullable(),
    prices: z.array(CmsPriceDTO),
  })
  .merge(Timestamps);
export type CmsProductDTO = z.infer<typeof CmsProductDTO>;

export const ProductId = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,63}$/, 'Expected a slug like plan_standard');
export const ProductParams = z.object({ id: ProductId });
export type ProductParams = z.infer<typeof ProductParams>;

/** `GET /cms/products` (admin) */
export const CmsProductsQuery = PageQuery.extend({ active: z.coerce.boolean().optional() });
export type CmsProductsQuery = z.infer<typeof CmsProductsQuery>;
export type CmsProductsQueryInput = z.input<typeof CmsProductsQuery>;

export const CmsProductsResponse = Page(CmsProductDTO);
export type CmsProductsResponse = z.infer<typeof CmsProductsResponse>;

/** `POST /cms/products` */
export const CmsProductCreateBody = z.object({
  id: ProductId,
  kind: ProductKind,
  name: I18nText,
  description: I18nText,
  features: z.array(z.string()).max(32),
  requiresPlan: z.boolean().default(false),
  active: z.boolean().default(true),
  sort: z.number().int().default(0),
});
export type CmsProductCreateBody = z.infer<typeof CmsProductCreateBody>;
export type CmsProductCreateBodyInput = z.input<typeof CmsProductCreateBody>;

/** `PATCH /cms/products/:id` */
export const CmsProductPatchBody = CmsProductCreateBody.omit({ id: true }).partial();
export type CmsProductPatchBody = z.infer<typeof CmsProductPatchBody>;

export const CmsProductResponse = z.object({ product: CmsProductDTO });
export type CmsProductResponse = z.infer<typeof CmsProductResponse>;

/** `DELETE /cms/products/:id` — only when no subscription references it. */
export const CmsProductDeleteResponse = ApiOk;
export type CmsProductDeleteResponse = z.infer<typeof CmsProductDeleteResponse>;

/** `POST /cms/products/:id/prices` */
export const CmsPriceCreateBody = z.object({
  interval: PriceInterval,
  amountMinor: z.number().int().nonnegative(),
  currency: Currency,
  active: z.boolean().default(true),
});
export type CmsPriceCreateBody = z.infer<typeof CmsPriceCreateBody>;
export type CmsPriceCreateBodyInput = z.input<typeof CmsPriceCreateBody>;

/** `PATCH /cms/prices/:id` — amounts are immutable once synced to Stripe; toggle `active`. */
export const CmsPricePatchBody = z.object({ active: z.boolean() });
export type CmsPricePatchBody = z.infer<typeof CmsPricePatchBody>;

export const CmsPriceResponse = z.object({ price: CmsPriceDTO });
export type CmsPriceResponse = z.infer<typeof CmsPriceResponse>;

/** `POST /cms/products/:id/sync-stripe` */
export const CmsProductSyncStripeResponse = z.object({
  product: CmsProductDTO,
  created: z.object({
    product: z.boolean(),
    prices: z.number().int().nonnegative(),
  }),
});
export type CmsProductSyncStripeResponse = z.infer<typeof CmsProductSyncStripeResponse>;
